import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Trophy, Calendar, Users, Gift, Crown, Ticket, Award, Star } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface Competition {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_completed: boolean;
  image_url?: string;
  rules_text?: string;
  is_multi_tenant?: boolean;
  creator_tenant_id?: string;
  tenant_id: string;
}

interface CompetitionPrize {
  id: string;
  competition_id: string;
  position: number;
  title: string;
  description?: string;
  value: string;
  image_url?: string;
}

interface CompetitionEntry {
  id: string;
  competition_id: string;
  customer_id: string;
  referral_lead_id: string;
  entry_date: string;
}

interface CompetitionWinner {
  id: string;
  competition_id: string;
  customer_id: string;
  position: number;
  prize_title: string;
  customer_name: string;
  drawn_at: string;
}

interface Customer {
  id: string;
  user_id: string;
  tenant_id: string;
  full_name: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface LeaderboardEntry {
  customer_id: string;
  customer_name: string;
  first_name: string;
  last_name: string;
  entry_count: number;
  rank: number;
}

export default function CompetitionView() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [prizes, setPrizes] = useState<{ [key: string]: CompetitionPrize[] }>({});
  const [myEntries, setMyEntries] = useState<{ [key: string]: CompetitionEntry[] }>({});
  const [winners, setWinners] = useState<{ [key: string]: CompetitionWinner[] }>({});
  const [leaderboard, setLeaderboard] = useState<{ [key: string]: LeaderboardEntry[] }>({});
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    if (user) {
      fetchCustomerData();
    }
  }, [user]);

  useEffect(() => {
    if (customer) {
      fetchCompetitions();
    }
  }, [customer]);

  const fetchCustomerData = async () => {
    try {
      const fullName = `${user!.user_metadata?.first_name || ''} ${user!.user_metadata?.last_name || ''}`.trim() || 'Kunde'
      
      // Use the new secure RPC function
      const { data, error } = await supabase.rpc('get_or_create_customer_profile', {
        p_user_id: user!.id,
        p_email: user!.email || '',
        p_full_name: fullName
      })

      if (error) throw error;

      if (data && data.length > 0) {
        setCustomer(data[0]);
      } else {
        throw new Error('Failed to get or create customer profile');
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      // Don't show toast - fail silently and let parent handle
    }
  };

  const fetchCompetitions = async () => {
    if (!customer) return;

    try {
      const { data, error } = await supabase
        .from('competitions')
        .select('*')
        .eq('tenant_id', customer.tenant_id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompetitions(data || []);

      // Fetch related data for each competition
      if (data && data.length > 0) {
        await Promise.all([
          fetchPrizes(data.map(c => c.id)),
          fetchMyEntries(data.map(c => c.id)),
          fetchWinners(data.map(c => c.id)),
          fetchLeaderboard(data.map(c => c.id))
        ]);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast({
        title: 'Fehler',
        description: 'Wettbewerbe konnten nicht geladen werden.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPrizes = async (competitionIds: string[]) => {
    if (competitionIds.length === 0) return;
    
    try {
      const { data, error } = await supabase.rpc('get_competition_prizes_secure', {
        p_competition_ids: competitionIds
      });

      if (error) {
        console.error('Error fetching prizes:', error);
        return;
      }

      if (data) {
        const prizesByCompetition = data.reduce((acc, prize) => {
          if (!acc[prize.competition_id]) acc[prize.competition_id] = [];
          acc[prize.competition_id].push(prize);
          return acc;
        }, {} as { [key: string]: CompetitionPrize[] });
        
        setPrizes(prizesByCompetition);
      }
    } catch (error) {
      console.error('Error in fetchPrizes:', error);
    }
  };

  const fetchMyEntries = async (competitionIds: string[]) => {
    if (!customer || competitionIds.length === 0) return;

    try {
      const { data, error } = await supabase.rpc('get_competition_entries_secure', {
        p_customer_id: customer.id,
        p_competition_ids: competitionIds
      });

      if (error) {
        console.error('Error fetching entries:', error);
        return;
      }

      // Group entries by competition
      const entriesByCompetition: { [key: string]: CompetitionEntry[] } = {};
      
      if (data) {
        data.forEach((entry) => {
          if (!entriesByCompetition[entry.competition_id]) {
            entriesByCompetition[entry.competition_id] = [];
          }
          
          entriesByCompetition[entry.competition_id].push({
            id: entry.entry_id,
            competition_id: entry.competition_id,
            customer_id: customer.id,
            referral_lead_id: entry.entry_type === 'referral' ? entry.entry_id : '',
            entry_date: entry.entry_date
          });
        });
      }
      
      setMyEntries(entriesByCompetition);
    } catch (error) {
      console.error('Error in fetchMyEntries:', error);
    }
  };

  const fetchWinners = async (competitionIds: string[]) => {
    const { data } = await supabase
      .from('competition_winners')
      .select('*')
      .in('competition_id', competitionIds);

    if (data) {
      // Fetch prize and customer details separately
      const prizeIds = data.map(winner => winner.prize_id);
      const customerIds = data.map(winner => winner.customer_id);

      const [prizeData, customerData] = await Promise.all([
        supabase.from('competition_prizes').select('id, title').in('id', prizeIds),
        supabase.from('customers').select('id, full_name').in('id', customerIds)
      ]);

      const prizeMap = (prizeData.data || []).reduce((acc, prize) => {
        acc[prize.id] = prize;
        return acc;
      }, {} as { [key: string]: { id: string; title: string } });

      const customerMap = (customerData.data || []).reduce((acc, cust) => {
        acc[cust.id] = cust;
        return acc;
      }, {} as { [key: string]: { id: string; full_name: string } });

      const winnersWithDetails = data.map(winner => ({
        ...winner,
        prize_title: prizeMap[winner.prize_id]?.title || '',
        customer_name: customerMap[winner.customer_id]?.full_name || ''
      }));

      const winnersByCompetition = winnersWithDetails.reduce((acc, winner) => {
        if (!acc[winner.competition_id]) acc[winner.competition_id] = [];
        acc[winner.competition_id].push(winner);
        return acc;
      }, {} as { [key: string]: CompetitionWinner[] });
      
      setWinners(winnersByCompetition);
    }
  };

  const fetchLeaderboard = async (competitionIds: string[]) => {
    if (!customer) return;
    
    try {
      const leaderboardByCompetition: { [key: string]: LeaderboardEntry[] } = {};
      
      for (const competitionId of competitionIds) {
        console.log(`Fetching leaderboard for competition ${competitionId}`);
        
        // Use the new secure function to get the leaderboard
        const { data: leaderboardData, error } = await supabase.rpc('get_competition_leaderboard', {
          p_competition_id: competitionId
        });

        if (error) {
          console.error(`Error fetching leaderboard for competition ${competitionId}:`, error);
          continue;
        }

        if (leaderboardData && leaderboardData.length > 0) {
          console.log(`Found ${leaderboardData.length} entries for competition ${competitionId}`);
          
          const leaderboardEntries: LeaderboardEntry[] = leaderboardData.map((entry, index) => ({
            customer_id: entry.customer_id,
            customer_name: entry.customer_name,
            first_name: entry.customer_name.split(' ')[0] || '',
            last_name: entry.customer_name.split(' ').slice(1).join(' ') || '',
            entry_count: entry.entry_count,
            rank: index + 1
          }));

          leaderboardByCompetition[competitionId] = leaderboardEntries;
        } else {
          console.log(`No leaderboard entries found for competition ${competitionId}`);
          leaderboardByCompetition[competitionId] = [];
        }
      }

      console.log('Final leaderboard data:', leaderboardByCompetition);
      setLeaderboard(leaderboardByCompetition);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast({
        title: 'Fehler',
        description: 'Rangliste konnte nicht geladen werden.',
        variant: 'destructive'
      });
    }
  };

  const getMyStats = (competitionId: string) => {
    const myCompetitionEntries = myEntries[competitionId] || [];
    const myTickets = myCompetitionEntries.length;
    const isWinner = (winners[competitionId] || []).some(w => w.customer_id === customer?.id);
    const myWinningPosition = (winners[competitionId] || []).find(w => w.customer_id === customer?.id)?.position;
    
    return { myTickets, isWinner, myWinningPosition };
  };

  const isCompetitionActive = (competition: Competition) => {
    const now = new Date();
    const startDate = new Date(competition.start_date);
    const endDate = new Date(competition.end_date);
    return now >= startDate && now <= endDate && competition.is_active && !competition.is_completed;
  };

  const isCompetitionUpcoming = (competition: Competition) => {
    const now = new Date();
    const startDate = new Date(competition.start_date);
    return now < startDate && competition.is_active && !competition.is_completed;
  };

  const isCompetitionCompleted = (competition: Competition) => {
    return competition.is_completed || (!competition.is_active && new Date() > new Date(competition.end_date));
  };

  const getCompetitionStatus = (competition: Competition) => {
    if (isCompetitionCompleted(competition)) return 'completed';
    if (isCompetitionActive(competition)) return 'active';
    if (isCompetitionUpcoming(competition)) return 'upcoming';
    return 'inactive';
  };

  const filterCompetitions = (status: string) => {
    return competitions.filter(competition => {
      const competitionStatus = getCompetitionStatus(competition);
      
      switch (status) {
        case 'active':
          return competitionStatus === 'active' || competitionStatus === 'upcoming';
        case 'completed':
          return competitionStatus === 'completed';
        default:
          return true;
      }
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Lade Wettbewerbe...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (competitions.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Keine Wettbewerbe verfügbar</h3>
          <p className="text-muted-foreground">
            Derzeit sind keine Wettbewerbe aktiv. Schauen Sie später wieder vorbei!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Wettbewerbe</h2>
        <p className="text-muted-foreground">
          Nehmen Sie an spannenden Wettbewerben teil und gewinnen Sie tolle Preise!
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Star className="w-4 h-4" />
            Aktive Wettbewerbe
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            Abgeschlossen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          <div className="grid gap-6">
            {filterCompetitions('active').map((competition) => {
              const stats = getMyStats(competition.id);
              const competitionPrizes = prizes[competition.id] || [];
              const status = getCompetitionStatus(competition);
              
              return (
                <Card key={competition.id} className="overflow-hidden">
                  <div className="relative">
                    {competition.image_url && (
                      <img 
                        src={competition.image_url} 
                        alt={competition.title}
                        className="w-full h-24 sm:h-32 object-contain"
                      />
                    )}
                    <div className="absolute top-4 left-4 flex gap-2">
                      <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                        {status === 'active' ? 'Läuft jetzt' : status === 'upcoming' ? 'Bald verfügbar' : 'Inaktiv'}
                      </Badge>
                      {competition.is_multi_tenant && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800">
                          <Users className="w-3 h-3 mr-1" />
                          Multi-Unternehmen
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          <Trophy className="w-5 h-5" />
                          {competition.title}
                        </CardTitle>
                        <CardDescription className="mt-2">
                          {competition.description}
                        </CardDescription>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                           <div className="flex items-center gap-1">
                             <Calendar className="w-4 h-4" />
                             {format(new Date(competition.start_date), 'dd.MM.yyyy', { locale: de })} - 
                             {format(new Date(competition.end_date), 'dd.MM.yyyy', { locale: de })}
                           </div>
                        </div>
                      </div>
                      
                      {stats.isWinner && (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          <Crown className="w-3 h-3 mr-1" />
                          Gewinner!
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <Tabs defaultValue="prizes" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="prizes">Preise</TabsTrigger>
                        <TabsTrigger value="leaderboard">Rangliste</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="prizes" className="mt-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {competitionPrizes.map((prize) => (
                            <Card key={prize.id} className="relative">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                  <Crown className="w-4 h-4" />
                                  {prize.position}. Platz
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  <h4 className="font-medium">{prize.title}</h4>
                                  <p className="text-sm text-primary font-medium">
                                    {prize.value && !prize.value.includes('€') ? `${prize.value} €` : prize.value}
                                  </p>
                                  {prize.description && (
                                    <p className="text-xs text-muted-foreground">{prize.description}</p>
                                  )}
                                  {prize.image_url && (
                                    <img 
                                      src={prize.image_url} 
                                      alt={prize.title}
                                      className="w-full h-20 object-contain rounded"
                                    />
                                  )}
                                </div>
                              </CardContent>
                              
                              {stats.isWinner && stats.myWinningPosition === prize.position && (
                                <div className="absolute inset-0 bg-yellow-500/10 border-2 border-yellow-500 rounded-lg flex items-center justify-center">
                                  <Badge variant="outline" className="bg-yellow-500 text-white border-yellow-500">
                                    <Crown className="w-3 h-3 mr-1" />
                                    Ihr Gewinn!
                                  </Badge>
                                </div>
                              )}
                            </Card>
                          ))}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="leaderboard" className="mt-4">
                        {(() => {
                          const competitionLeaderboard = leaderboard[competition.id] || [];
                          const myPosition = competitionLeaderboard.find(entry => entry.customer_id === customer?.id);
                          
                          const formatDisplayName = (entry: LeaderboardEntry) => {
                            const isMyself = entry.customer_id === customer?.id;
                            if (isMyself) {
                              // Show full name for myself
                              return entry.customer_name;
                            } else {
                              // Show first name + first letter of last name for others
                              const lastNameInitial = entry.last_name ? entry.last_name.charAt(0) + '.' : '';
                              return `${entry.first_name} ${lastNameInitial}`;
                            }
                          };

                          return (
                            <div className="space-y-4">
                              {myPosition && (
                                <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-full text-white font-bold text-sm">
                                        {myPosition.rank}
                                      </div>
                                      <div>
                                        <p className="font-medium text-primary">Ihr aktueller Platz</p>
                                        <p className="text-sm text-muted-foreground">
                                          {myPosition.entry_count} {myPosition.entry_count === 1 ? 'Los' : 'Lose'}
                                        </p>
                                      </div>
                                    </div>
                                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                                      <Trophy className="w-3 h-3 mr-1" />
                                      Platz {myPosition.rank}
                                    </Badge>
                                  </div>
                                </div>
                              )}
                              
                              <div className="space-y-2">
                                <h4 className="font-medium flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  Rangliste ({competitionLeaderboard.length} Teilnehmer)
                                </h4>
                                
                                <div className="max-h-64 overflow-y-auto space-y-1">
                                  {competitionLeaderboard.map((entry) => {
                                    const isMyself = entry.customer_id === customer?.id;
                                    
                                    return (
                                      <div 
                                        key={entry.customer_id}
                                        className={`flex items-center justify-between p-3 rounded-lg border ${
                                          isMyself 
                                            ? 'bg-primary/10 border-primary/20 text-primary font-medium' 
                                            : 'bg-muted/20 border-muted'
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                            isMyself 
                                              ? 'bg-primary text-white' 
                                              : 'bg-muted text-muted-foreground'
                                          }`}>
                                            {entry.rank}
                                          </div>
                                          <span className={isMyself ? 'text-primary font-medium' : ''}>
                                            {formatDisplayName(entry)}
                                          </span>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm">
                                            {entry.entry_count} {entry.entry_count === 1 ? 'Los' : 'Lose'}
                                          </span>
                                          {entry.rank <= 3 && (
                                            <Trophy className={`w-4 h-4 ${
                                              entry.rank === 1 ? 'text-yellow-500' :
                                              entry.rank === 2 ? 'text-gray-400' :
                                              'text-orange-400'
                                            }`} />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                
                                {competitionLeaderboard.length === 0 && (
                                  <div className="text-center py-8 text-muted-foreground">
                                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>Noch keine Teilnehmer</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                       </TabsContent>
                     </Tabs>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          <div className="grid gap-6">
            {filterCompetitions('completed').map((competition) => {
              const stats = getMyStats(competition.id);
              const competitionWinners = winners[competition.id] || [];
              
              return (
                <Card key={competition.id} className="opacity-75">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          <Trophy className="w-5 h-5" />
                          {competition.title}
                          <Badge variant="outline">Abgeschlossen</Badge>
                        </CardTitle>
                        <CardDescription className="mt-2">
                          {competition.description}
                        </CardDescription>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                           <div className="flex items-center gap-1">
                             <Calendar className="w-4 h-4" />
                             {format(new Date(competition.start_date), 'dd.MM.yyyy', { locale: de })} - 
                             {format(new Date(competition.end_date), 'dd.MM.yyyy', { locale: de })}
                           </div>
                        </div>
                      </div>
                      
                      {stats.isWinner && (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          <Crown className="w-3 h-3 mr-1" />
                          {stats.myWinningPosition}. Platz
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {competitionWinners.length > 0 ? (
                      <div className="space-y-3">
                        <h4 className="font-medium">Gewinner:</h4>
                        {competitionWinners
                          .sort((a, b) => a.position - b.position)
                          .map((winner) => (
                            <div 
                              key={winner.id} 
                              className={`flex items-center justify-between p-3 rounded-lg border ${
                                winner.customer_id === customer?.id 
                                  ? 'bg-yellow-50 border-yellow-200' 
                                  : 'bg-muted/50'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-8 h-8 bg-yellow-500 text-white rounded-full text-sm font-bold">
                                  {winner.position}
                                </div>
                                <div>
                                  <p className="font-medium">
                                    {winner.customer_id === customer?.id ? 'Sie' : winner.customer_name}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{winner.prize_title}</p>
                                </div>
                              </div>
                              {winner.customer_id === customer?.id && (
                                <Crown className="w-5 h-5 text-yellow-500" />
                              )}
                            </div>
                          ))
                        }
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        Keine Gewinner bekannt gegeben.
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}